import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { formatDate } from '../lib/utils';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiInbox, FiClock, FiCheckCircle, FiXCircle, FiPackage, FiShield } from 'react-icons/fi';

const STATUS_TABS = [
  { key: 'PENDING', label: 'Pending', icon: FiClock, variant: 'warning' },
  { key: 'RECEIVED', label: 'Received', icon: FiPackage, variant: 'primary' },
  { key: 'CONVERTED', label: 'Converted', icon: FiCheckCircle, variant: 'success' },
  { key: 'CANCELED', label: 'Canceled', icon: FiXCircle, variant: 'error' },
  { key: 'ALL', label: 'All', icon: FiInbox, variant: 'default' }
];

function statusBadgeVariant(status) {
  switch (status) {
    case 'PENDING': return 'warning';
    case 'RECEIVED': return 'primary';
    case 'CONVERTED': return 'success';
    case 'CANCELED': return 'error';
    default: return 'default';
  }
}

export default function IncomingOrders() {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('PENDING');
  const [search, setSearch] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ pending: 0, received: 0 });

  const fetchData = async () => {
    if (!tenant) return;
    setLoading(true);
    try {
      const status = activeTab === 'ALL' ? undefined : activeTab;
      const [data, stats] = await Promise.all([
        api.getPendingOrders(tenant.id, { status, search }),
        api.getPendingOrdersStats(tenant.id)
      ]);
      setOrders(data);
      setCounts(stats);
    } catch (err) {
      console.error('Fetch incoming orders error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tenant, activeTab]);

  // Debounced search
  useEffect(() => {
    if (!tenant) return;
    const t = setTimeout(fetchData, 250);
    return () => clearTimeout(t);
  }, [search]);

  const tabBadgeCount = (key) => {
    if (key === 'PENDING') return counts.pending;
    if (key === 'RECEIVED') return counts.received;
    return null;
  };

  const declaredFirearmSummary = (order) => {
    const parts = [
      order.declared_manufacturer,
      order.declared_model,
      order.declared_caliber
    ].filter(Boolean);
    if (parts.length === 0) return <span className="text-slate-400">—</span>;
    return parts.join(' • ');
  };

  const ageInDays = (createdAt) => {
    if (!createdAt) return null;
    const ms = Date.now() - new Date(createdAt).getTime();
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
          Incoming Orders
        </h2>
        <p className="text-sm text-slate-500">
          Pending order notices from your WooCommerce site. When the rifle arrives,
          mark received then verify and convert to a bound book acquisition.
        </p>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          const badge = tabBadgeCount(tab.key);
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-700'
              }`}
            >
              <SafeIcon icon={tab.icon} className="h-4 w-4" />
              {tab.label}
              {badge !== null && badge > 0 && (
                <span className={`inline-flex items-center justify-center rounded-full px-1.5 text-xs font-bold ${
                  isActive ? 'bg-white text-primary-700' : 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-400'
                }`}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by customer name, serial, or order #"
          className="pl-9"
        />
      </div>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-sm text-slate-500">Loading…</div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center">
              <SafeIcon icon={FiInbox} className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">
                {activeTab === 'PENDING'
                  ? 'No pending orders. New WooCommerce orders will appear here automatically.'
                  : `No ${activeTab.toLowerCase()} orders.`}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Order #</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Declared Firearm</th>
                    <th className="px-4 py-3">Serial</th>
                    <th className="px-4 py-3">Service</th>
                    <th className="px-4 py-3">Expected</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Received</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                  {orders.map((order) => (
                    <tr
                      key={order.id}
                      onClick={() => navigate(`/incoming-orders/${order.id}`)}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40"
                    >
                      <td className="px-4 py-3 font-mono text-xs">
                        {order.external_order_id}
                        <div className="text-[10px] text-slate-400 mt-0.5">{ageInDays(order.created_at)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 dark:text-white">{order.declared_customer_name}</div>
                        {order.declared_email && (
                          <div className="text-xs text-slate-500">{order.declared_email}</div>
                        )}
                        {order.ffl_dealer && (
                          <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-primary-600 dark:text-primary-400">
                            <SafeIcon icon={FiShield} className="h-3 w-3" />
                            FFL: {order.ffl_dealer.business_name || order.ffl_dealer.license_name || order.ffl_dealer.ffl_dashed}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">{declaredFirearmSummary(order)}</td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {order.declared_is_non_serialized
                          ? <span className="text-slate-400 italic">NSN</span>
                          : (order.declared_serial_number || <span className="text-slate-400">—</span>)
                        }
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {order.service_description || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                        {order.expected_arrival_date ? formatDate(order.expected_arrival_date) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={statusBadgeVariant(order.status)}>{order.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {order.received_at ? formatDate(order.received_at) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
